// src/components/SystemRecommendation/components/AssumptionsSection.tsx

import React from 'react';
import { Assumption, ConfidenceScore, ViewMode } from '../types';

interface AssumptionsSectionProps {
  assumptions: Assumption[];
  confidence: ConfidenceScore;
  viewMode: ViewMode;
}

const AssumptionsSection: React.FC<AssumptionsSectionProps> = ({
  assumptions,
  confidence,
  viewMode
}) => {

  if (viewMode === 'customer') {
    return (
      <div className="assumptions-section customer">
        <h2 className="section-title">Before You Decide</h2>

        <div className="customer-assumptions-content">
          <p className="intro-text">
            This recommendation is based on what we know about your home today.
          </p>

          <div className="assumptions-summary">
            <h3>We've made some assumptions:</h3>
            <ul className="assumptions-list">
              {assumptions
                .filter(a => a.needsVerification)
                .map((assumption, index) => (
                  <li key={index} className="assumption-item">
                    {assumption.description}
                  </li>
                ))}
            </ul>
          </div>

          <div className="survey-promise">
            <h3>On the day of survey, we'll:</h3>
            <ul className="survey-checks">
              <li>✓ Measure everything properly</li>
              <li>✓ Check your radiators and pipework</li>
              <li>✓ Confirm the cylinder will fit</li>
            </ul>

            <p className="customer-control">
              If anything changes, we'll discuss it with you before we start work.
            </p>

            <p className="customer-control-emphasis">
              <strong>You're in control of the decision.</strong>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Technical mode
  return (
    <div className="assumptions-section technical">
      <h2 className="section-title">What We've Assumed</h2>

      <div className="technical-assumptions-content">
        <div className="assumptions-list-technical">
          {assumptions.map((assumption, index) => (
            <div
              key={index}
              className={`assumption-card ${assumption.confidence}`}
            >
              <div className="assumption-header">
                <span className="assumption-icon">
                  {assumption.needsVerification ? '⚠️' : '✓'}
                </span>
                <h3 className="assumption-type">
                  {assumption.type.replace(/([A-Z])/g, ' $1').trim()}:
                </h3>
              </div>

              <p className="assumption-description">
                {assumption.description}
              </p>

              <p className="assumption-impact">
                <strong>Impact:</strong> {assumption.impact}
              </p>

              {assumption.needsVerification && (
                <div className="verification-needed">
                  <span className="verification-badge">Verification needed</span>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="confidence-breakdown">
          <h3 className="confidence-title">Confidence Breakdown</h3>

          <div className="confidence-calculation">
            {confidence.breakdown.map((item, index) => (
              <div key={index} className="confidence-item">
                <span className="factor-name">{item.factor}:</span>
                <span className={`impact ${item.impact >= 0 ? 'positive' : 'negative'}`}>
                  {item.impact >= 0 ? '+' : ''}{item.impact}%
                </span>
                <span className="factor-description">{item.description}</span>
              </div>
            ))}

            <div className="confidence-total">
              <span className="total-label">Final Confidence:</span>
              <span className={`total-value ${confidence.level}`}>
                {confidence.percentage}% ({confidence.level})
              </span>
            </div>
          </div>
        </div>

        <div className="next-steps-technical">
          <h3>Verification Required:</h3>
          <ul className="verification-list">
            {assumptions
              .filter(a => a.needsVerification)
              .map((assumption, index) => (
                <li key={index}>
                  {assumption.type === 'heatLoss' && 'Full heat loss survey recommended'}
                  {assumption.type === 'radiators' && 'Radiator audit required'}
                  {assumption.type === 'cylinderSpace' && 'Cylinder space measurement needed'}
                  {assumption.type === 'gasSupply' && 'Gas pressure test on day of install'}
                  {assumption.type === 'pipework' && 'Pipework inspection required'}
                  {assumption.type === 'propertyAge' && 'Property construction survey needed'}
                </li>
              ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AssumptionsSection;
